import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { Controller, FormProvider, useFormContext, useFormState, ControllerProps, FieldValues, FieldPath, FormProviderProps } from "react-hook-form";
import { toast } from "sonner";

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

const ErrorWatcher = () => {
    const { formState } = useFormContext();
    const { errors, submitCount } = formState;

    React.useEffect(() => {
        if (submitCount > 0 && Object.keys(errors).length > 0) {
            const firstErrorKey = Object.keys(errors)[0];
            if (firstErrorKey) {
                const fieldError = errors[firstErrorKey] as any;
                let errorObj = fieldError;
                let actualErrorKey = firstErrorKey;
                while (errorObj && !errorObj.message && typeof errorObj === "object") {
                    const nestedKeys = Object.keys(errorObj);
                    if (nestedKeys.length > 0) {
                        actualErrorKey = `${actualErrorKey}.${nestedKeys[0]}`;
                        errorObj = errorObj[nestedKeys[0]];
                    } else {
                        break;
                    }
                }
                const errorMessage = errorObj?.message || "Thông tin nhập vào chưa hợp lệ.";

                toast.warning(`Lỗi nhập liệu: ${errorMessage}`, {
                    id: "form-validation-error",
                });

                setTimeout(() => {
                    const element =
                        document.getElementsByName(actualErrorKey)[0] ||
                        document.getElementById(actualErrorKey) ||
                        document.querySelector(`[name$="${actualErrorKey}"]`) ||
                        document.querySelector(`[name="${firstErrorKey}"]`);
                    if (element) {
                        (element as any).focus();
                        if (typeof (element as any).select === "function") {
                            (element as any).select();
                        }
                    }
                }, 50);
            }
        }
    }, [submitCount, errors]);

    return null;
};

const Form = <
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues extends FieldValues | undefined = undefined
>({
    children,
    ...props
}: FormProviderProps<TFieldValues, TContext, TTransformedValues>) => {
    return (
        <FormProvider {...props}>
            <ErrorWatcher />
            {children}
        </FormProvider>
    );
};

const FormFieldContext = React.createContext<any>({})

const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
    {
        ...props
    }: ControllerProps<TFieldValues, TName>
) => {
    return (
        <FormFieldContext.Provider value={{ name: props.name }}>
            <Controller {...props} />
        </FormFieldContext.Provider>
    );
}

const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext)
    const itemContext = React.useContext(FormItemContext)
    const { getFieldState } = useFormContext()
    const formState = useFormState({ name: fieldContext.name })
    const fieldState = getFieldState(fieldContext.name, formState)

    if (!fieldContext) {
        throw new Error("useFormField should be used within <FormField>")
    }

    const { id } = itemContext

    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    }
}

const FormItemContext = React.createContext<any>({})

function FormItem({
    className,
    ...props
}: React.ComponentProps<"div">) {
    const id = React.useId()

    return (
        <FormItemContext.Provider value={{ id }}>
            {/* minmax(0,1fr): ô nhập không giãn theo chữ dài (VD gợi ý của ô chọn) làm tràn hộp thoại trên điện thoại */}
            <div data-slot="form-item" className={cn("grid grid-cols-[minmax(0,1fr)] gap-2", className)} {...props} />
        </FormItemContext.Provider>
    );
}

function FormLabel({
    className,
    ...props
}: React.ComponentProps<typeof Label>) {
    const { error, formItemId } = useFormField()

    return (
        <Label
            data-slot="form-label"
            data-error={!!error}
            className={cn("data-[error=true]:text-destructive", className)}
            htmlFor={formItemId}
            {...props} />
    );
}

function FormControl({
    ...props
}: React.ComponentProps<typeof Slot>) {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

    return (
        <Slot
            data-slot="form-control"
            id={formItemId}
            aria-describedby={
                !error
                    ? `${formDescriptionId}`
                    : `${formDescriptionId} ${formMessageId}`
            }
            aria-invalid={!!error}
            {...props} />
    );
}

function FormDescription({
    className,
    ...props
}: React.ComponentProps<"p">) {
    const { formDescriptionId } = useFormField()

    return (
        <p
            data-slot="form-description"
            id={formDescriptionId}
            className={cn("text-muted-foreground text-sm", className)}
            {...props} />
    );
}

function FormMessage({
    className,
    ...props
}: React.ComponentProps<"p">) {
    const { error, formMessageId } = useFormField()
    const body = error ? String(error?.message ?? "") : props.children

    if (!body) {
        return null
    }

    return (
        <p
            data-slot="form-message"
            id={formMessageId}
            className={cn("text-destructive text-sm", className)}
            {...props}>
            {body}
        </p>
    );
}

export {
    useFormField,
    Form,
    FormItem,
    FormLabel,
    FormControl,
    FormDescription,
    FormMessage,
    FormField,
}
